import { useState } from "react";
import axios from "axios";
import MessageBubble from "./MessageBubble";
import SuggestionCard from "./SuggestionCard";
import DoctorCard from "../DoctorCard";
import { useUser } from "../../context/UserContext";
import { Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

function timeLabel() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const ChatUI = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [latestSuggestion, setLatestSuggestion] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useUser();

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    setError("");
    setMessages((prev) => [...prev, { role: "user", text: trimmed, timestamp: timeLabel() }]);
    setMessage("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/api/ai/symptoms`,
        { message: trimmed },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          : undefined
      );

      const ai = res.data;
      setLatestSuggestion(ai);
      setDoctors(Array.isArray(ai.doctors) ? ai.doctors : []);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: ai.summary || "I could not generate suggestions at the moment.",
          timestamp: timeLabel()
        }
      ]);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      const friendlyMessage = backendMessage || "Unable to fetch AI suggestions right now. Please try again.";
      setError(friendlyMessage);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: friendlyMessage,
          timestamp: timeLabel()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onEnterToSend = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <section className="xl:col-span-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col min-h-[70vh]">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Symptom Chat</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Share symptoms to get non-diagnostic guidance and doctor specialties.</p>
        </div>

        <div className="flex-1 p-4 space-y-4 overflow-y-auto">
          {!messages.length && (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800">
              Try: "I have persistent cough and mild fever for 4 days."
            </div>
          )}

          {messages.map((item, index) => (
            <MessageBubble key={`${item.role}-${index}`} role={item.role} text={item.text} timestamp={item.timestamp} />
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-bounce" />
              <span className="h-2 w-2 rounded-full bg-green-500 animate-bounce [animation-delay:120ms]" />
              <span className="h-2 w-2 rounded-full bg-green-500 animate-bounce [animation-delay:240ms]" />
              AI assistant is thinking...
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-end gap-3">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={onEnterToSend}
              rows={2}
              placeholder="Type your symptoms..."
              className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-4 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleSend}
              disabled={loading || !message.trim()}
              className="rounded-xl bg-green-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      </section>

      <aside className="xl:col-span-1 space-y-4">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">AI Suggestions</h3>
          <SuggestionCard suggestion={latestSuggestion} />
        </div>

        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Recommended Doctors</h3>
          <div className="mt-4 space-y-4">
            {!doctors.length && <p className="text-sm text-gray-500 dark:text-gray-400">Doctors will appear after AI analysis.</p>}
            {doctors.map((doctor) => (
              <div key={doctor._id}>
                <DoctorCard
                  doctor={{
                    _id: doctor._id,
                    name: doctor.name,
                    speciality: doctor.specialty,
                    clinic: doctor.hospitalName,
                    location: doctor.location,
                    status: doctor.availability ? "Available" : "Not Available"
                  }}
                />
                <div className="mt-2 flex gap-2">
                  <Link
                    to={user ? `/book-appointment/${doctor._id}` : `/login/user?redirect=/book-appointment/${doctor._id}`}
                    className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700"
                  >
                    Book Appointment
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default ChatUI;
